import { MsalGuardConfiguration, MsalInterceptorConfiguration } from "@azure/msal-angular";
import { BrowserCacheLocation, IPublicClientApplication, PublicClientApplication, LogLevel, InteractionType, AuthenticationResult, Configuration } from "@azure/msal-browser";

import { app, authentication } from "@microsoft/teams-js";

import { InsightsService } from "@core/services/Insights.service";
import { environment } from "environments/environment"

export const isIE = window.navigator.userAgent.includes('MSIE ') || window.navigator.userAgent.includes('Trident/');
export const isSafari = window.navigator.userAgent.includes("Safari");

const scopes = [`https://mariposacbc.onmicrosoft.com/${environment.appClientID}/user.read`];

export const msalConfig: Configuration = {
  auth: {
    clientId: environment.appClientID, // PPE testing environment
    authority: `https://login.microsoftonline.com/${environment.appTenantID}`, // PPE testing environment.
    // navigateToLoginRequestUrl: true
  },
  cache: {
    cacheLocation: BrowserCacheLocation.LocalStorage,
    // storeAuthStateInCookie: isIE || isSafari, // set to true for IE 11. Remove this line to use Angular Universal
  },
  system: {
    loggerOptions: {
      loggerCallback,
      logLevel: LogLevel.Info,
      piiLoggingEnabled: false
    }
  }
};

export function loggerCallback(logLevel: LogLevel, message: string) {
    console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(msalConfig);
}

/**
 * Destino al que hay que llevar al usuario después de volver de Entra. Lo toma
 * app.component una vez que el token quedó listo (ver takeRedirectReturnUrl).
 */
let redirectReturnUrl: string | null = null;

/**
 * Entrega, una sola vez, el destino pendiente tras volver de Entra.
 * @returns Devuelve la url a la que hay que navegar, o null si no hay ninguna
 */
export function takeRedirectReturnUrl(): string | null {
  const url = redirectReturnUrl;
  redirectReturnUrl = null;
  return url;
}

/** Última vez que se mandó al usuario a Entra sin que él lo pidiera */
const REDIRECT_GUARD_KEY = 'brain.msal.lastAutoRedirect';

/** Ventana en la que no se repite un redirect automático, en milisegundos */
const REDIRECT_GUARD_WINDOW = 30000;

/**
 * Indica si se puede mandar al usuario a Entra de forma automática y, si se
 * puede, deja registrado el intento.
 *
 * Es el freno contra el bucle de recargas: si el token no se pudo renovar
 * justo después de volver de Entra, otro redirect solo repite lo mismo. Se
 * guarda en sessionStorage porque sobrevive a los redirects dentro de la
 * misma pestaña y se limpia al cerrarla.
 *
 * @returns Devuelve false si ya hubo un redirect automático hace muy poco
 */
export function tryAutoRedirect(): boolean {
  try {
    const last = Number(sessionStorage.getItem(REDIRECT_GUARD_KEY) || 0);
    if (Date.now() - last < REDIRECT_GUARD_WINDOW) return false;

    sessionStorage.setItem(REDIRECT_GUARD_KEY, String(Date.now()));
  } catch {
    // Sin sessionStorage (navegación privada estricta) no hay cómo frenar el
    // bucle: se deja pasar, como antes
  }

  return true;
}

/**
 * Olvida el último redirect automático. Se llama cuando el token se renovó
 * bien o cuando el usuario inicia sesión a mano.
 */
export function resetAutoRedirect(): void {
  try {
    sessionStorage.removeItem(REDIRECT_GUARD_KEY);
  } catch {
    // Nada que limpiar
  }
}

/**
 * Inicializa MSAL y procesa la respuesta de Entra, si la página viene de un redirect.
 *
 * @param msalInstance Instancia de MSAL
 * @param insights Servicio de Application Insights, para dejar rastro de los fallos
 */
export async function initializeMsal(msalInstance: IPublicClientApplication, insights?: InsightsService): Promise<void> {
  await msalInstance.initialize();

  let response: AuthenticationResult | null = null;

  try {
    response = await msalInstance.handleRedirectPromise();
  } catch (error) {
    // Antes esto tumbaba el arranque y dejaba la pantalla en blanco. Se deja
    // rastro y la app sigue: sin cuenta, termina en el login
    insights?.traceError(environment.projects.Login, 'Error al procesar el regreso de Entra ID', authContext(), authError(error), null);
    return;
  }

  if (!response?.account) return;

  const urlParams = new URLSearchParams(window.location.search);
  const returnUrl = urlParams.get('returnUrl') || '/';

  if (app.isInitialized()) {
    const auth = JSON.stringify({ username: response.account.username, accessToken: response.accessToken, expiresOn: response.expiresOn });
    authentication.notifySuccess(auth);
  }
  msalInstance.setActiveAccount(response.account);

  insights?.traceInfo(environment.projects.Login, 'Volvió de Entra ID con sesión iniciada', { ...authContext(), returnUrl }, null);

  // No se recarga la página para ir al destino: la recarga competía con el
  // arranque de Angular, que pide el token en paralelo, y en Safari de iOS eso
  // terminaba en un bucle de redirects o en una pantalla en blanco. La app
  // arranca acá mismo y navega con el router cuando el token queda listo
  if (`${window.location.pathname}${window.location.search}` !== returnUrl) {
    redirectReturnUrl = returnUrl;
  }
}

/**
 * Contexto que acompaña a las trazas de inicio de sesión. Sin datos personales.
 */
export function authContext(): { [key: string]: any } {
  return {
    path: window.location.pathname,
    isSafari,
    userAgent: window.navigator.userAgent
  };
}

/**
 * Resume un error de MSAL para la traza: el código es lo que dice qué falló.
 * @param error Error que devolvió MSAL
 */
export function authError(error: any): { [key: string]: any } {
  return {
    name: error?.name,
    errorCode: error?.errorCode,
    subError: error?.subError,
    message: error?.errorMessage || error?.message
  };
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft-ppe.com/v1.0/me', scopes);
  protectedResourceMap.set('https://api.powerbi.com/', ['https://analysis.windows.net/powerbi/api/.default']);
  protectedResourceMap.set(`${environment.securityApiUrl}/api`, ['user.read', 'openid']);
  protectedResourceMap.set(`${environment.commonApiUrl}/api`, ['user.read', 'openid']);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return { 
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: scopes,
      redirectUri: environment.loginRedirectUriAD
    },
    loginFailedRoute: '/pages/miscellaneous/not-authorized'
  };
}
